import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Dislike, Timer1, TimerPause, Verify, Menu, AttachCircle, MessageText1 } from 'iconsax-react';
import { Dropdown, MenuProps, Popover, message, Skeleton } from 'antd';
import TitleText from '../../atoms/TitleText';
import InfoText from '../../atoms/InfoText';
import DeleteModal from '../../atoms/DeleteModal';
import { useDeletePostMutation, useGetPostDislikeReasonsQuery } from '../../../store/services/biddingPostsService';
import { PostSummary } from '../../../types/biddingPosts';

const STATUS_STYLES: Record<string, { className: string; icon: React.ReactNode }> = {
  PUBLISHED: {
    className: 'bg-blue-50 dark:bg-blue-950/40 text-light-information dark:text-dark-information',
    icon: <Verify size={18} variant="Bold" color="currentColor" />,
  },
  NEW: {
    className: 'bg-blue-50 dark:bg-blue-950/40 text-light-information dark:text-dark-information',
    icon: <Verify size={18} variant="Bold" color="currentColor" />,
  },
  ONGOING: {
    className: 'bg-yellow-50 dark:bg-yellow-950/40 text-light-alert dark:text-dark-alert',
    icon: <TimerPause size={18} variant="Bold" color="currentColor" />,
  },
  IN_PROGRESS: {
    className: 'bg-yellow-50 dark:bg-yellow-950/40 text-light-alert dark:text-dark-alert',
    icon: <TimerPause size={18} variant="Bold" color="currentColor" />,
  },
  COMPLETED: {
    className: 'bg-green-50 dark:bg-green-950/40 text-light-success dark:text-dark-success',
    icon: <Verify size={18} variant="Bold" color="currentColor" />,
  },
};

const DEFAULT_STATUS_STYLE = {
  className:
    'bg-soft-background dark:bg-dark-surface-primary text-light-text-secondary dark:text-dark-text-secondary border border-light-input-border dark:border-dark-input-border',
  icon: <Verify size={18} variant="Bold" color="currentColor" />,
};

interface BiddingPostHeaderProps {
  postId: string;
  summary?: PostSummary;
  isLoading?: boolean;
}

export default function BiddingPostHeader({ postId, summary, isLoading }: BiddingPostHeaderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const [deletePost, { isLoading: isDeleting }] = useDeletePostMutation();

  // ── API: Dislike Reasons ─────────────────────────────────────────────────
  const { data: dislikeReasonsData } = useGetPostDislikeReasonsQuery(
    { postId },
    { skip: !postId },
  );

  const dislikedReasons = useMemo(() => {
    if (dislikeReasonsData?.payload?.reasons?.length) {
      return dislikeReasonsData.payload.reasons.map((r) => ({
        id: r.reasonId,
        reason: r.name,
        count: r.count,
      }));
    }
    return [];
  }, [dislikeReasonsData]);

  const totalDislikes = dislikeReasonsData?.payload?.totalDislikes ?? 0;
  const canDelete = summary?.actions?.canDelete ?? false;

  const postedDateFormatted = useMemo(
    () => (summary?.createdAt ? dayjs(summary.createdAt).format('MMM DD, YYYY [at] h:mm a') : '-'),
    [summary?.createdAt],
  );

  const closesDateFormatted = useMemo(
    () => (summary?.closesAt ? dayjs(summary.closesAt).format('MMM DD, YYYY') : '-'),
    [summary?.closesAt],
  );

  const dislikedReasonsContent = (
    <div
      className="w-[300px] md:w-[320px] p-2 space-y-1 bg-white dark:bg-dark-surface-primary"
      data-testid="bidding-post-disliked-reasons-popover"
    >
      <div className="pb-2 px-1" data-testid="bidding-post-disliked-reasons-header">
        <InfoText
          text={t('biddingPosts.dislikedReasonsTitle')}
          className="!text-sm !font-normal text-light-text-secondary dark:text-dark-text-secondary"
        />
      </div>

      <div className="space-y-0.5" data-testid="bidding-post-disliked-reasons-list">
        {dislikedReasons.map((item) => (
          <div
            key={item.id}
            className="flex items-center justify-between py-2.5 px-2.5 rounded-[8px] border-b border-light-input-border/50 dark:border-dark-input-border/50 last:border-b-0 gap-3 cursor-pointer hover:bg-soft-background dark:hover:bg-gray-800/60 transition-colors"
            data-testid={`bidding-post-disliked-reason-item-${item.id}`}
          >
            <InfoText
              text={item.reason}
              className="!text-sm !font-normal text-light-text-primary dark:text-dark-text-primary"
            />
            <div
              className="min-w-[24px] h-[22px] px-2 rounded-[6px] bg-red-100 dark:bg-red-950/50 text-light-warning dark:text-dark-warning flex items-center justify-center"
              data-testid={`bidding-post-disliked-reason-count-${item.id}`}
            >
              <InfoText
                text={`${item.count}`}
                className="!text-xs !font-medium text-light-warning dark:text-dark-warning leading-none"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const handleConfirmDelete = async () => {
    try {
      await deletePost({ postId }).unwrap();
      setIsDeleteModalOpen(false);
      navigate('/bidding-posts');
    } catch (error) {
      console.error(error);
      message.error(t('common.error'));
      setIsDeleteModalOpen(false);
    }
  };

  const menuItems: NonNullable<MenuProps['items']> = [
    {
      key: 'title',
      label: (
        <div
          onClick={(e) => e.stopPropagation()}
          className="px-3 pt-1 pb-1 cursor-default select-none pointer-events-none"
          data-testid="post-header-management-title"
        >
          <InfoText
            text={t('biddingPosts.postManagement')}
            className="!text-xs text-light-text-secondary dark:text-dark-text-secondary !font-normal"
          />
        </div>
      ),
      disabled: true,
      className:
        '!bg-transparent hover:!bg-transparent focus:!bg-transparent active:!bg-transparent [&.ant-dropdown-menu-item]:!bg-transparent [&.ant-dropdown-menu-item:hover]:!bg-transparent [&.ant-dropdown-menu-item-active]:!bg-transparent !cursor-default !p-0 mb-1 pointer-events-none',
    },
    ...(canDelete
      ? [
          {
            key: 'delete',
            label: (
              <div
                className="px-3 py-2 cursor-pointer"
                data-testid={`post-header-action-delete-${postId}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDeleteModalOpen(true);
                }}
              >
                <InfoText
                  text={t('biddingPosts.deletePost')}
                  className="!text-sm !font-bold text-light-text-primary dark:text-dark-text-primary"
                />
              </div>
            ),
            className:
              '!p-0 !rounded-[8px] hover:!bg-pink-100 dark:hover:!bg-pink-950/40 [&.ant-dropdown-menu-item-active]:!bg-pink-100 dark:[&.ant-dropdown-menu-item-active]:!bg-pink-950/40 transition-colors',
          },
        ]
      : [
          {
            key: 'no-actions',
            label: (
              <div
                className="px-3 py-2 cursor-default select-none pointer-events-none"
                data-testid={`post-header-action-no-actions-${postId}`}
              >
                <InfoText
                  text={t('biddingPosts.noActions')}
                  className="!text-sm !font-normal text-light-text-placeholder dark:text-dark-text-placeholder"
                />
              </div>
            ),
            disabled: true,
            className:
              '!p-0 !bg-transparent hover:!bg-transparent cursor-default pointer-events-none',
          },
        ]),
  ];

  const statusCode = summary?.status?.code?.toUpperCase() || '';
  const statusConfig = STATUS_STYLES[statusCode] || DEFAULT_STATUS_STYLE;

  const proposalTagText = useMemo(() => {
    const count = summary?.proposalNo ?? 0;
    return count === 1
      ? t('biddingPosts.proposalTagSingular', { count })
      : t('biddingPosts.proposalTagPlural', { count });
  }, [summary?.proposalNo, t]);

  const shortlistedTagText = useMemo(() => {
    const count = summary?.shortlistedNo ?? 0;
    return t('biddingPosts.shortlistedTag', { count });
  }, [summary?.shortlistedNo, t]);

  const invitedTagText = useMemo(() => {
    const count = summary?.invitedNo ?? 0;
    return count === 1
      ? t('biddingPosts.invitedSellerTagSingular', { count })
      : t('biddingPosts.invitedSellerTagPlural', { count });
  }, [summary?.invitedNo, t]);

  if (isLoading || !summary) {
    return (
      <div
        className="bg-white dark:bg-dark-surface-primary rounded-[4px] p-6 space-y-4 border-b border-light-input-border dark:border-dark-input-border"
        data-testid="bidding-post-header-skeleton"
      >
        <Skeleton active paragraph={{ rows: 3 }} />
      </div>
    );
  }

  return (
    <div
      className="bg-white dark:bg-dark-surface-primary rounded-[16px] p-6 space-y-4 border border-light-input-border dark:border-dark-input-border relative overflow-hidden"
      data-testid="bidding-post-header-container"
    >
      {/* Top Header Row */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4" data-testid="bidding-post-header-top-row">
        <div className="space-y-1 flex-1 min-w-0" data-testid="bidding-post-header-info">
          <InfoText
            text={`${t('biddingPosts.postId')} : ${summary.serial?.startsWith('#') ? summary.serial : `#${summary.serial ?? ''}`}`}
            className="!text-sm !font-normal text-light-text-secondary dark:text-dark-text-secondary whitespace-nowrap"
            data-testid="bidding-post-header-serial"
          />
          <TitleText
            text={summary.title ?? ''}
            className="!text-2xl !font-bold text-light-text-primary dark:text-dark-text-primary break-words"
            data-testid="bidding-post-header-title"
          />
        </div>

        <div className="flex items-center gap-3 self-start shrink-0" data-testid="bidding-post-header-actions">
          {/* Disliked Count Pill with Popover - only show when totalDislikes > 0 */}
          {totalDislikes > 0 && (
            <Popover
              content={dislikedReasonsContent}
              trigger="click"
              placement="bottomRight"
              overlayClassName="[&_.ant-popover-inner]:!p-2 [&_.ant-popover-inner]:!rounded-[16px] [&_.ant-popover-inner]:!bg-white dark:[&_.ant-popover-inner]:!bg-dark-surface-primary [&_.ant-popover-inner]:!shadow-[0_12px_32px_rgba(0,0,0,0.12)] [&_.ant-popover-inner]:!border [&_.ant-popover-inner]:!border-light-input-border dark:[&_.ant-popover-inner]:!border-dark-input-border"
              arrow={false}
            >
              <button
                type="button"
                className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-red-50 dark:bg-red-950/40 text-light-warning dark:text-dark-warning !text-xs !font-semibold cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                data-testid="bidding-post-header-dislikes"
              >
                <Dislike size={20} variant="Bold" color="currentColor" />
                <InfoText
                  text={t('biddingPosts.dislikedCount', { count: totalDislikes })}
                  className="!text-xs !font-semibold text-light-warning dark:text-dark-warning"
                />
              </button>
            </Popover>
          )}

          {/* Action Menu Button matching Figma Frame 44x44px */}
          <Dropdown
            menu={{
              items: menuItems,
              className:
                '!w-[180px] !p-2 !rounded-[16px] !border !border-light-input-border dark:!border-dark-input-border !shadow-[0_8px_24px_rgba(0,0,0,0.08)] bg-white dark:!bg-dark-surface-primary [&_.ant-dropdown-menu-item-disabled]:!bg-transparent [&_.ant-dropdown-menu-item-disabled:hover]:!bg-transparent [&_.ant-dropdown-menu-item-disabled]:!cursor-default [&_.ant-dropdown-menu-item:not(.ant-dropdown-menu-item-disabled):hover]:!bg-pink-100 dark:[&_.ant-dropdown-menu-item:not(.ant-dropdown-menu-item-disabled):hover]:!bg-pink-950/40 [&_.ant-dropdown-menu-item:not(.ant-dropdown-menu-item-disabled).ant-dropdown-menu-item-active]:!bg-pink-100 dark:[&_.ant-dropdown-menu-item:not(.ant-dropdown-menu-item-disabled).ant-dropdown-menu-item-active]:!bg-pink-950/40',
            }}
            trigger={['click']}
            placement="bottomRight"
            onOpenChange={setIsMenuOpen}
          >
            <button
              type="button"
              className={`group w-[44px] h-[44px] rounded-[12px] flex items-center justify-center p-[12px] gap-[8px] cursor-pointer transition-colors ${isMenuOpen
                  ? 'bg-pink-500 border border-pink-500 text-black-950 dark:text-black-950'
                  : 'bg-black-500 dark:bg-gray-800 border border-black-500 dark:border-gray-700 text-light-button-label-primary hover:bg-pink-500 hover:border-pink-500 hover:text-black-950 dark:hover:text-black-950'
                }`}
              data-testid="bidding-post-header-menu-trigger"
            >
              <Menu
                size={20}
                variant="Linear"
                color="currentColor"
                className={`transition-colors ${isMenuOpen
                    ? '!text-black-950 dark:!text-black-950'
                    : '!text-light-button-label-primary group-hover:!text-black-950 dark:group-hover:!text-black-950'
                  }`}
              />
            </button>
          </Dropdown>
        </div>
      </div>

      {/* Status & Dates Badges Row */}
      <div className="flex flex-wrap items-center gap-2.5" data-testid="bidding-post-header-badges">
        {/* Status Badge */}
        <div
          className={`h-[36px] px-[16px] py-[8px] rounded-[8px] text-sm font-normal inline-flex items-center gap-[8px] ${statusConfig.className}`}
          data-testid="bidding-post-status-badge"
        >
          {statusConfig.icon}
          <InfoText text={summary?.status?.name || '-'} className="!text-sm !font-normal" />
        </div>

        {/* Posted Date Badge */}
        <div
          className="h-[36px] px-[24px] py-[8px] rounded-[8px] text-sm font-normal bg-blue-50 dark:bg-blue-950/40 text-light-information dark:text-dark-information inline-flex items-center gap-[8px]"
          data-testid="bidding-post-posted-badge"
        >
          <Timer1 size={20} variant="Bold" color="currentColor" />
          <InfoText text={t('biddingPosts.postedAt', { date: postedDateFormatted })} className="!text-sm !font-normal text-light-information dark:text-dark-information" />
        </div>

        {/* Closes Date Badge */}
        <div
          className="h-[36px] px-[24px] py-[8px] rounded-[8px] text-sm font-normal bg-yellow-100 dark:bg-yellow-950/40 text-light-alert dark:text-dark-alert inline-flex items-center gap-[8px]"
          data-testid="bidding-post-closes-badge"
        >
          <TimerPause size={20} variant="Bold" color="currentColor" />
          <InfoText text={t('biddingPosts.closesAt', { date: closesDateFormatted })} className="!text-sm !font-normal text-light-alert dark:text-dark-alert" />
        </div>
      </div>

      {/* Description */}
      <InfoText
        text={summary.description ?? ''}
        className="!text-sm !font-normal text-light-text-secondary dark:text-dark-text-secondary !leading-relaxed"
        data-testid="bidding-post-header-desc"
      />

      {/* Metric Tags Row */}
      <div className="flex flex-wrap items-center gap-3 pt-1" data-testid="bidding-post-header-metrics">
        <div
          className="flex items-center gap-1.5 text-light-information dark:text-dark-information"
          data-testid="bidding-post-proposal-tag"
        >
          <MessageText1 size={16} variant="Bold" color="currentColor" />
          <InfoText
            text={proposalTagText}
            className="!text-[13px] sm:!text-[14px] !font-normal text-light-information dark:text-dark-information"
          />
        </div>

        <div
          className="h-4 w-px bg-gray-300 dark:bg-gray-700 self-center"
          data-testid="bidding-post-metric-divider-1"
        />

        <div
          className="flex items-center gap-1.5 text-light-success dark:text-dark-success"
          data-testid="bidding-post-shortlisted-tag"
        >
          <AttachCircle size={18} variant="Bold" color="currentColor" />
          <InfoText
            text={shortlistedTagText}
            className="!text-[13px] sm:!text-[14px] !font-normal text-light-success dark:text-dark-success"
          />
        </div>

        <div
          className="h-4 w-px bg-gray-300 dark:bg-gray-700 self-center"
          data-testid="bidding-post-metric-divider-2"
        />

        <div
          className="flex items-center gap-1.5 text-light-information dark:text-dark-information"
          data-testid="bidding-post-invited-tag"
        >
          <MessageText1 size={16} variant="Bold" color="currentColor" />
          <InfoText
            text={invitedTagText}
            className="!text-[13px] sm:!text-[14px] !font-normal text-light-information dark:text-dark-information"
          />
        </div>
      </div>

      {/* Delete Confirmation Modal (Admin action without coins deduction logic) */}
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onDelete={handleConfirmDelete}
        isLoading={isDeleting}
      />
    </div>
  );
}
